// Écran de connexion (étape d) : identifiant + mot de passe, puis code de double authentification.
// Première connexion (avec le code donné par Nils) : choix du mot de passe, installation de la
// double authentification (QR code), codes de secours à noter.
import React, { useEffect, useState } from "react";

const ENCRE = "#281E1E";
const TAUPE = "#8C8282";
const PIERRE = "#F2EFED";
const ROUILLE = "#A8502F";

const styles = {
  page: { minHeight: "100vh", background: PIERRE, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif", color: ENCRE },
  carte: { background: "#fff", borderRadius: 12, padding: "28px 24px", width: "100%", maxWidth: 400, boxSizing: "border-box", boxShadow: "0 2px 12px rgba(40,30,30,0.08)" },
  titre: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, margin: "0 0 4px" },
  sousTitre: { fontSize: 13, color: TAUPE, margin: "0 0 20px", lineHeight: 1.5 },
  label: { display: "block", fontSize: 12, color: TAUPE, margin: "12px 0 4px" },
  champ: { width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 15, border: `1px solid ${PIERRE}`, borderRadius: 8, background: "#FBFAF9", color: ENCRE, fontFamily: "inherit" },
  bouton: { width: "100%", marginTop: 20, padding: "11px 12px", fontSize: 14, fontWeight: 600, background: ENCRE, color: PIERRE, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" },
  erreur: { marginTop: 14, fontSize: 13, color: ROUILLE, lineHeight: 1.4 },
  aide: { fontSize: 12, color: TAUPE, marginTop: 6, lineHeight: 1.5 },
};

export default function Connexion({ session, etapeInitiale = "identifiant", nom = "", logo, onConnecte }) {
  const [etape, setEtape] = useState(etapeInitiale);
  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [code, setCode] = useState("");
  const [totp, setTotp] = useState(null); // { qr, secret }
  const [codesSecours, setCodesSecours] = useState(null);
  const [codesNotes, setCodesNotes] = useState(false);
  const [erreur, setErreur] = useState("");
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (etape !== "totp" || totp) return;
    session.preparerDoubleAuthentification().then(setTotp, (e) => setErreur(e.message));
  }, [etape, totp, session]);

  async function agir(fn) {
    setErreur("");
    setEnCours(true);
    try {
      await fn();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  }
  const suite = (r) => {
    setCode("");
    if (r.etape === "complete") onConnecte();
    else setEtape(r.etape);
  };

  let contenu;
  if (etape === "identifiant") {
    contenu = (
      <form onSubmit={(e) => { e.preventDefault(); agir(async () => { const r = await session.connexion(identifiant, motDePasse); setMotDePasse(""); suite(r); }); }}>
        <h1 style={styles.titre}>Connexion</h1>
        <p style={styles.sousTitre}>ERP Stone Concept</p>
        <label style={styles.label} htmlFor="identifiant">Identifiant</label>
        <input id="identifiant" style={styles.champ} autoComplete="username" autoCapitalize="none" value={identifiant} onChange={(e) => setIdentifiant(e.target.value)} required />
        <label style={styles.label} htmlFor="mdp">Mot de passe</label>
        <input id="mdp" style={styles.champ} type="password" autoComplete="current-password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required />
        <div style={styles.aide}>Première connexion ou mot de passe oublié : saisis le code de connexion donné par Nils à la place du mot de passe.</div>
        <button type="submit" style={styles.bouton} disabled={enCours}>{enCours ? "Vérification…" : "Se connecter"}</button>
      </form>
    );
  } else if (etape === "mdp") {
    contenu = (
      <form onSubmit={(e) => { e.preventDefault(); if (motDePasse !== confirmation) { setErreur("Les deux mots de passe ne sont pas identiques."); return; } agir(async () => suite(await session.choisirMotDePasse(motDePasse))); }}>
        <h1 style={styles.titre}>Choisis ton mot de passe</h1>
        <p style={styles.sousTitre}>Au moins 10 caractères, sans ton identifiant. Une phrase courte que tu retiens facilement fait un bon mot de passe. Personne d'autre que toi ne le connaîtra, pas même le Gérant.</p>
        <label style={styles.label} htmlFor="nmdp">Nouveau mot de passe</label>
        <input id="nmdp" style={styles.champ} type="password" autoComplete="new-password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required minLength={10} />
        <label style={styles.label} htmlFor="cmdp">Le même, une deuxième fois</label>
        <input id="cmdp" style={styles.champ} type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required />
        <button type="submit" style={styles.bouton} disabled={enCours}>Continuer</button>
      </form>
    );
  } else if (etape === "totp") {
    contenu = (
      <form onSubmit={(e) => { e.preventDefault(); agir(async () => { const r = await session.activerDoubleAuthentification(code); setCode(""); setCodesSecours(r.codesSecours); setEtape("codes"); }); }}>
        <h1 style={styles.titre}>Double authentification</h1>
        <p style={styles.sousTitre}>
          À chaque connexion, en plus du mot de passe, l'ERP te demandera un code à 6 chiffres affiché par ton téléphone.
          Installe une application de codes (iPhone : l'application « Mots de passe » d'Apple ; Android : « Google Authenticator », utilisable sans compte), puis scanne ce code :
        </p>
        <div style={{ textAlign: "center", minHeight: 220 }}>
          {totp ? <img src={totp.qr} alt="QR code à scanner avec l'application de codes" width={220} height={220} /> : <div style={{ ...styles.aide, paddingTop: 90 }}>Préparation…</div>}
        </div>
        {totp && <div style={{ ...styles.aide, textAlign: "center", wordBreak: "break-all" }}>Impossible de scanner ? Saisis cette clé dans l'application : <strong style={{ color: ENCRE, letterSpacing: 1 }}>{totp.secret}</strong></div>}
        <label style={styles.label} htmlFor="code">Code affiché par l'application</label>
        <input id="code" style={{ ...styles.champ, letterSpacing: 4, textAlign: "center" }} inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
        <button type="submit" style={styles.bouton} disabled={enCours || !totp}>Activer</button>
      </form>
    );
  } else if (etape === "codes") {
    contenu = (
      <div>
        <h1 style={styles.titre}>Codes de secours</h1>
        <p style={styles.sousTitre}>
          Si tu perds ou changes de téléphone, chacun de ces codes permet <strong style={{ color: ENCRE }}>une</strong> connexion à la place du code à 6 chiffres.
          Note-les sur papier et range-les en lieu sûr : ils ne seront plus jamais affichés.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 15, background: PIERRE, borderRadius: 8, padding: 14, textAlign: "center" }}>
          {(codesSecours || []).map((c) => (<div key={c}>{c}</div>))}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 16, lineHeight: 1.4 }}>
          <input type="checkbox" checked={codesNotes} onChange={(e) => setCodesNotes(e.target.checked)} style={{ marginTop: 3 }} />
          J'ai noté mes codes de secours.
        </label>
        <button type="button" style={{ ...styles.bouton, opacity: codesNotes ? 1 : 0.5 }} disabled={!codesNotes} onClick={() => onConnecte()}>Entrer dans l'ERP</button>
      </div>
    );
  } else {
    // etape === "code"
    contenu = (
      <form onSubmit={(e) => { e.preventDefault(); agir(async () => suite(await session.saisirCode(code))); }}>
        <h1 style={styles.titre}>Code de vérification</h1>
        <p style={styles.sousTitre}>{nom ? `${nom}, saisis` : "Saisis"} le code à 6 chiffres affiché par ton application de codes.</p>
        <label style={styles.label} htmlFor="code2">Code</label>
        <input id="code2" style={{ ...styles.champ, letterSpacing: 4, textAlign: "center" }} autoComplete="one-time-code" maxLength={12} value={code} onChange={(e) => setCode(e.target.value)} autoFocus required />
        <div style={styles.aide}>Téléphone perdu ? Saisis un de tes codes de secours (ex. ABCD-EFGH).</div>
        <button type="submit" style={styles.bouton} disabled={enCours}>Valider</button>
      </form>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.carte}>
        {logo && <img src={logo} alt="Stone Concept" style={{ height: 36, width: "auto", display: "block", marginBottom: 22 }} />}
        {contenu}
        {erreur && <div role="alert" style={styles.erreur}>{erreur}</div>}
        {etape !== "identifiant" && etape !== "codes" && (
          <button type="button" onClick={() => session.deconnecter()} style={{ ...styles.aide, background: "none", border: "none", padding: 0, marginTop: 18, cursor: "pointer", textDecoration: "underline" }}>
            Annuler et revenir à la connexion
          </button>
        )}
      </div>
    </div>
  );
}
